import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormConfigStore } from '../form-config/state/store';
import { usePipelineSteps } from '../hooks/usePipelineSteps';
import { AutomationEditorDrawer } from './AutomationEditorDrawer';
import { fieldsWithAppendedBehavior, fieldsWithReplacedBehavior, fieldsWithRemovedBehavior } from './automation-write';
import { bucketForEffect } from './effect-bucket';
import { FILL_TRANSFORM_EFFECTS } from './effect-kind';
import { collectDerivedChecks } from './derived-display';
import { checkStageDependencyOrder, type DependencyOrderWarning } from '../form-config/rules/dependencyOrderCheck';
import { AutomationList, type AutomationListForm } from './list/AutomationList';
import { ConstantsSection } from './ConstantsSection';
import type { Behavior, Stage, CreateRuleAttachmentInput, RuleAttachment, Automation } from '@capture/shared';
import { type Surface, filterBySurface } from '@capture/shared';
import './automations.css';

/**
 * gap #12 — dependency-order (stale-value) warnings for ONE form, across all
 * its stages. Reuses the pure `checkStageDependencyOrder` engine (do not
 * reimplement). Dependency ordering is per-form: `fieldRef` is a field within
 * one form, and two forms may reuse the same field name, so we never mix forms
 * and build the label resolver from THIS form's fields (name → label).
 */
function collectFormWarnings(
  rules: readonly RuleAttachment[],
  fields: readonly { name: string; label?: string }[],
): DependencyOrderWarning[] {
  const labelByRef = new Map<string, string>();
  for (const f of fields) if (f.name) labelByRef.set(f.name, f.label || f.name);
  const byStage = new Map<string, RuleAttachment[]>();
  for (const r of rules) {
    const list = byStage.get(r.stage) ?? [];
    list.push(r);
    byStage.set(r.stage, list);
  }
  const out: DependencyOrderWarning[] = [];
  for (const [, stageRules] of byStage) {
    out.push(...checkStageDependencyOrder(stageRules, (ref) => labelByRef.get(ref)));
  }
  return out;
}

export function AutomationsTab({ surface = 'standard' }: { surface?: Surface }) {
  const navigate = useNavigate();
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  // SP1 §5.2 — the palette is surface-scoped. With zero declared remittance
  // effects in SP1 this is behavior-identical to today for both surfaces.
  const fillEffects = filterBySurface(FILL_TRANSFORM_EFFECTS, surface);
  const updateFormFields = useFormConfigStore((s) => s.updateFormFields);
  const updateRuleInForm = useFormConfigStore((s) => s.updateRuleInForm);
  const deleteRuleFromForm = useFormConfigStore((s) => s.deleteRuleFromForm);
  const replaceAutomationRules = useFormConfigStore((s) => s.replaceAutomationRules);
  const pendingAutomationDraft = useFormConfigStore((s) => s.pendingAutomationDraft);
  const setPendingAutomationDraft = useFormConfigStore((s) => s.setPendingAutomationDraft);
  const jobTypeId = useFormConfigStore((s) => s.version?.jobTypeId);
  const { steps: pipelineStepRows } = usePipelineSteps(jobTypeId);
  const pipelineSteps = useMemo(
    () => pipelineStepRows.map((s) => ({ stepType: s.stepType, label: s.label })),
    [pipelineStepRows],
  );
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [editing, setEditing] = useState<
    {
      effectKey: string;
      // Hand-off preset (a field-editor / fill-intent jump): seeds the NEW
      // drawer's form + field so it opens on the field the operator jumped
      // from, not the default first field. Absent for a palette pick.
      preset?: { formId: string; fieldName: string };
      edit?: { formId: string; fieldName: string; stage: Stage; behaviorIndex: number; behavior: Behavior };
    } | null
  >(null);

  // P3c — field-editor → Automations hand-off. When "Add a custom check" in the
  // field drawer navigates here, it leaves a `pendingAutomationDraft` in the
  // store. A fill_transform effect (e.g. 'compute') opens the
  // AutomationEditorDrawer via `editing`, seeded to the handed-off form + field
  // so it opens on the field the operator jumped from. Everything else hands
  // off to the E-062 create flow carrying the effect + field context as
  // `?effect=&field=` — the flow pre-answers WHERE/ON WHAT from the effect's
  // registry defaults and lands on IF/THEN.
  useEffect(() => {
    if (!pendingAutomationDraft) return;
    const bucket = bucketForEffect(pendingAutomationDraft.effectKey);
    if (bucket === 'fill_transform') {
      setEditing({
        effectKey: pendingAutomationDraft.effectKey,
        preset: {
          formId: pendingAutomationDraft.formId,
          fieldName: pendingAutomationDraft.fieldName,
        },
      });
    } else {
      const params = new URLSearchParams({
        form: pendingAutomationDraft.formId,
        effect: pendingAutomationDraft.effectKey,
        field: pendingAutomationDraft.fieldName,
      });
      // E-035 — forward the fields-screen return context so the stepper's
      // Cancel/Finish bounce back to where the bridge was launched from.
      if (pendingAutomationDraft.returnTo) {
        params.set('returnTo', pendingAutomationDraft.returnTo);
      }
      navigate(`/admin/job-types/${jobTypeId}/automations/new?${params.toString()}`);
    }
    setPendingAutomationDraft(null);
  }, [pendingAutomationDraft, setPendingAutomationDraft, navigate, jobTypeId]);

  function handleSaveNew(formId: string, fieldName: string, stage: Stage, behavior: Behavior) {
    const nextFields = fieldsWithAppendedBehavior(contentV3, formId, fieldName, stage, behavior);
    if (!nextFields) return; // form/field vanished — keep the drawer open, don't fake success
    updateFormFields(formId, nextFields);
    setEditing(null);
  }

  function handleSaveEdit(formId: string, fieldName: string, stage: Stage, behavior: Behavior) {
    const target = editing?.edit;
    if (!target) return;
    const nextFields = fieldsWithReplacedBehavior(contentV3, formId, fieldName, stage, target.behaviorIndex, behavior);
    if (!nextFields) return;
    updateFormFields(formId, nextFields);
    setEditing(null);
  }

  function handlePalettePick(effectKey: string) {
    setPaletteOpen(false);
    setEditing({ effectKey });
  }

  function handleDelete() {
    const target = editing?.edit;
    if (!target) return;
    const nextFields = fieldsWithRemovedBehavior(contentV3, target.formId, target.fieldName, target.stage, target.behaviorIndex);
    if (!nextFields) return;
    updateFormFields(target.formId, nextFields);
    setEditing(null);
  }

  function handleNewAutomation() {
    const formId = contentV3?.forms[0]?.id ?? '';
    navigate(`/admin/job-types/${jobTypeId}/automations/new?form=${formId}`);
  }

  function handleEditAutomation(automation: Automation, formId: string) {
    const key = automation.groupId ?? automation.branches.find((b) => b.ruleId)?.ruleId;
    if (!key) return;
    navigate(`/admin/job-types/${jobTypeId}/automations/${key}?form=${formId}`);
  }

  function handleEditBehavior(row: {
    formId: string; fieldName: string; stage: Stage; behaviorIndex: number; behavior: Behavior; effectKey: string;
  }) {
    setEditing({
      effectKey: row.effectKey,
      edit: { formId: row.formId, fieldName: row.fieldName, stage: row.stage, behaviorIndex: row.behaviorIndex, behavior: row.behavior },
    });
  }

  function handleToggleActive(formId: string, ruleIds: string[], nextActive: boolean) {
    for (const ruleId of ruleIds) updateRuleInForm(formId, ruleId, { isActive: nextActive });
  }

  function handleDuplicateAutomation(formId: string, inputs: CreateRuleAttachmentInput[]) {
    replaceAutomationRules(formId, [], inputs);
  }

  function handleDeleteAutomation(formId: string, ruleIds: string[]) {
    for (const ruleId of ruleIds) deleteRuleFromForm(formId, ruleId);
  }

  // gap #12 — dependency-order (stale-value) warnings, computed per form from
  // the current ruleset (advisory only; no gating, no auto-fix).
  const warningsByForm = useMemo(() => {
    const map = new Map<string, DependencyOrderWarning[]>();
    for (const form of contentV3?.forms ?? []) {
      const w = collectFormWarnings(form.rules ?? [], form.fields ?? []);
      if (w.length > 0) map.set(form.id, w);
    }
    return map;
  }, [contentV3]);

  const derivedRows = collectDerivedChecks(contentV3);
  const derivedByForm = new Map<string, typeof derivedRows>();
  for (const d of derivedRows) {
    const arr = derivedByForm.get(d.formId) ?? [];
    arr.push(d);
    derivedByForm.set(d.formId, arr);
  }

  const formOrder = new Map<string, string>();
  for (const f of contentV3?.forms ?? []) formOrder.set(f.id, f.name);
  const renderFormIds = [...formOrder.keys()].filter(
    (id) => derivedByForm.has(id) || warningsByForm.has(id),
  );

  // Memoized on `contentV3` so AutomationList's own `useMemo([forms, ...])`
  // isn't defeated by a fresh array/object identity on every render (e.g.
  // toggling the palette) when the underlying content hasn't changed.
  const listForms: AutomationListForm[] = useMemo(
    () => (contentV3?.forms ?? []).map((f) => ({
      id: f.id, name: f.name, fields: f.fields ?? [], rules: f.rules ?? [],
    })),
    [contentV3],
  );

  return (
    <div className="automations-tab">
      <div className="automations-tab__intro">
        Automations that fill, transform, validate, or route this job&apos;s documents.
        Field checks below are read-only — edit them on the Fields tab.
      </div>

      <div className="automations-tab__toolbar">
        <button className="btn btn--primary" data-testid="automation-new-btn" onClick={handleNewAutomation}>
          + New automation
        </button>
        <button className="btn" data-testid="automation-add-btn" onClick={() => setPaletteOpen((o) => !o)}>
          + Fill or fix a value
        </button>
        {paletteOpen && (
          <div className="automations-palette" role="menu">
            <div className="automations-palette__group-title">Fill / transform</div>
            {fillEffects.map((e) => (
              <button key={e.key} className="automations-palette__chip"
                data-testid={`automation-palette-${e.key}`} onClick={() => handlePalettePick(e.key)}>
                {e.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {renderFormIds.map((formId) => {
        const formWarnings = warningsByForm.get(formId) ?? [];
        return (
          <div key={formId}>
            <div className="automations-tab__form-title">
              {formOrder.get(formId)}
              {formWarnings.length > 0 && (
                <span
                  className="automations-warning-badge"
                  data-testid={`automation-warning-badge-${formId}`}
                  title={`${formWarnings.length} possible stale-values issue${formWarnings.length === 1 ? '' : 's'}`}
                >
                  {`⚠ ${formWarnings.length}`}
                </span>
              )}
            </div>
            {formWarnings.length > 0 && (
              <div
                className="automations-warning-panel"
                role="status"
                data-testid={`automation-warning-panel-${formId}`}
              >
                <div className="automations-warning-panel__title">
                  {'⚠ Possible stale-values issue'}
                </div>
                <ul className="automations-warning-panel__list">
                  {formWarnings.map((w) => (
                    <li
                      key={`${w.consumerRuleId}-${w.transformRuleId}`}
                      className="automations-warning-panel__item"
                      data-testid={`automation-warning-item-${w.consumerRuleId}`}
                    >
                      {w.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(derivedByForm.get(formId) ?? []).length > 0 && (
              <div className="automations-derived" data-testid={`automation-derived-${formId}`}>
                <div className="automations-derived__title">Field checks · set on the Fields tab</div>
                {(derivedByForm.get(formId) ?? []).map((d, i) => (
                  <div className="automations-row automations-row--derived" key={`${d.fieldLabel}-${d.sourceLabel}-${i}`}>
                    <span className="automations-row__label">{d.sourceLabel}</span>
                    <span className="automations-row__field">{d.fieldLabel}</span>
                    <span className="automations-row__summary">{d.summary}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* P8 — job-level named constants authoring (referenced by conditions /
          `{const.*}` expressions; publish gates on undeclared references). */}
      <ConstantsSection />

      <AutomationList
        forms={listForms}
        pipelineSteps={pipelineSteps}
        onEdit={handleEditAutomation}
        onEditBehavior={handleEditBehavior}
        onToggleActive={handleToggleActive}
        onDuplicate={handleDuplicateAutomation}
        onDelete={handleDeleteAutomation}
      />

      {editing && (
        <AutomationEditorDrawer
          key={editing.edit ? `edit-${editing.edit.formId}-${editing.edit.fieldName}-${editing.edit.stage}-${editing.edit.behaviorIndex}` : `new-${editing.effectKey}-${editing.preset?.formId ?? ''}-${editing.preset?.fieldName ?? ''}`}
          effectKey={editing.effectKey}
          contentV3={contentV3}
          initial={editing.edit}
          preset={editing.preset}
          onSave={editing.edit ? handleSaveEdit : handleSaveNew}
          onDelete={editing.edit ? handleDelete : undefined}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
